// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// Single contact detail page.
//
// Shows the full data we have on a contact: name, company/title, all
// emails/phones/addresses/urls, notes, dates, tags, provenance, and
// (if applicable) the link to the client this contact was converted into.
//
// Actions:
//   * Convert to Client — navigates to the existing Create Client page
//     with the contact's data pre-filled via query params. After the
//     client is successfully created over there, that page calls
//     adminApi.linkContactToClient(contactId, clientId) to record the
//     link back on this contact.
//   * Delete — single-contact delete (with confirmation).

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const SOURCE_LABELS = {
  vcard_apple:   'Apple Contacts (vCard)',
  vcard_google:  'Google Contacts (vCard)',
  vcard_generic: 'vCard',
  google_csv:    'Google Contacts (CSV)',
  outlook_csv:   'Outlook (CSV)',
  manual:        'Manual entry',
};

const labelStyle = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#888', fontFamily: 'Arial, sans-serif', fontWeight: 600, marginBottom: 4 };
const sectionStyle = { marginTop: 18 };

export default function AdminContactDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    adminApi.getContact(id)
      .then(c => { if (!cancelled) { setContact(c); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  async function handleDelete() {
    if (!window.confirm(`Permanently delete "${contact.display_name}"?`)) return;
    try {
      await adminApi.deleteContact(id);
      navigate('/admin/dashboard/contacts');
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  function handleConvert() {
    if (!contact) return;
    // Pre-fill the Create Client form via query params. The Create Client
    // page reads these on mount and seeds form state. The `fromContact`
    // param tells it which contact to link back to after creation.
    const params = new URLSearchParams();
    params.set('fromContact', contact.id);
    params.set('name',  contact.display_name || '');
    const primaryEmail = (contact.emails || [])[0]?.value || '';
    const primaryPhone = (contact.phones || [])[0]?.value || '';
    if (primaryEmail) params.set('email', primaryEmail);
    if (primaryPhone) params.set('phone', primaryPhone);
    navigate(`/admin/create-client?${params.toString()}`);
  }

  if (loading) {
    return <div style={{ padding: 24, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;
  }
  if (error) {
    return (
      <div>
        <div className="alert alert-error">{error}</div>
        <Link to="/admin/dashboard/contacts" className="btn">← Back to Contacts</Link>
      </div>
    );
  }
  if (!contact) return null;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/admin/dashboard/contacts" style={{ fontSize: 13, color: '#2f5496' }}>← Back to Contacts</Link>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 4 }}>{contact.display_name}</h1>
          {(contact.title || contact.company) && (
            <div style={{ color: '#555', fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
              {[contact.title, contact.company].filter(Boolean).join(' — ')}
            </div>
          )}
        </div>
        <div className="btn-row" style={{ marginTop: 0 }}>
          {contact.converted_to_client_id ? (
            <Link to={`/admin/clients/${contact.converted_to_client_id}`} className="btn btn-primary">
              Open Client →
            </Link>
          ) : (
            <button className="btn btn-primary" onClick={handleConvert}>
              Convert to Client
            </button>
          )}
          <button className="btn" style={{ color: '#B85042' }} onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>

      {/* Converted notice */}
      {contact.converted_to_client_id && (
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          This contact was converted into a client
          {contact.converted_client_name ? <> — <strong>{contact.converted_client_name}</strong></> : null}
          {contact.converted_at ? <> on {new Date(contact.converted_at).toLocaleDateString()}</> : null}.
        </div>
      )}

      <div className="card">
        <FieldSection title="Emails" items={contact.emails} render={e => (
          <a href={`mailto:${e.value}`} style={{ color: '#2f5496' }}>{e.value}</a>
        )} typeLabel />

        <FieldSection title="Phones" items={contact.phones} render={p => (
          <a href={`tel:${p.value}`} style={{ color: '#2f5496' }}>{p.value}</a>
        )} typeLabel />

        <FieldSection title="Addresses" items={contact.addresses} render={a => {
          const parts = [
            a.street,
            [a.city, a.state, a.postal_code].filter(Boolean).join(' '),
            a.country,
          ].filter(Boolean);
          return <span>{parts.join(' • ')}</span>;
        }} typeLabel />

        <FieldSection title="Websites" items={contact.urls} render={u => (
          <a href={u.value} target="_blank" rel="noreferrer" style={{ color: '#2f5496' }}>{u.value}</a>
        )} typeLabel />

        <FieldSection title="Important dates" items={contact.dates} render={d => (
          <span>{capitalize(d.type)}: {d.value}</span>
        )} />

        {(contact.tags || []).length > 0 && (
          <div style={sectionStyle}>
            <div style={labelStyle}>Tags</div>
            <div>
              {(contact.tags || []).map((t, i) => (
                <span key={i} style={{
                  display: 'inline-block',
                  padding: '3px 10px',
                  marginRight: 6,
                  marginBottom: 4,
                  background: '#eef0f4',
                  borderRadius: 10,
                  fontSize: 12,
                  fontFamily: 'Arial, sans-serif',
                }}>{t}</span>
              ))}
            </div>
          </div>
        )}

        {contact.notes && (
          <div style={sectionStyle}>
            <div style={labelStyle}>Notes</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#222' }}>
              {contact.notes}
            </div>
          </div>
        )}
      </div>

      {/* Provenance — minor, lives at the bottom. */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={labelStyle}>Provenance</div>
        <table style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#555' }}>
          <tbody>
            <tr><td style={{ padding: '2px 12px 2px 0' }}>Source</td><td>{SOURCE_LABELS[contact.source] || contact.source}</td></tr>
            {contact.source_file && (
              <tr><td style={{ padding: '2px 12px 2px 0' }}>Source file</td><td>{contact.source_file}</td></tr>
            )}
            {contact.imported_at && (
              <tr><td style={{ padding: '2px 12px 2px 0' }}>Imported</td><td>{new Date(contact.imported_at).toLocaleString()}</td></tr>
            )}
            {contact.source_uid && (
              <tr><td style={{ padding: '2px 12px 2px 0' }}>Source UID</td><td><code style={{ fontSize: 11 }}>{contact.source_uid}</code></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// A small reusable section that renders a labeled list with the per-item
// "type" badge to the left.
function FieldSection({ title, items, render, typeLabel }) {
  if (!items || !items.length) return null;
  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>{title}</div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {items.map((item, i) => (
          <li key={i} style={{ marginBottom: 4, fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
            {typeLabel && item.type && (
              <span style={{
                display: 'inline-block',
                minWidth: 60,
                marginRight: 8,
                fontSize: 11,
                color: '#888',
                textTransform: 'capitalize',
              }}>{prettyType(item.type)}</span>
            )}
            {render(item)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function prettyType(t) {
  if (!t) return '';
  if (t === 'fax_home')  return 'Home fax';
  if (t === 'fax_work')  return 'Work fax';
  if (t === 'fax_other') return 'Fax';
  return String(t).replace(/_/g, ' ');
}

function capitalize(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }
